import { Controller } from "@hotwired/stimulus";

// Wraps a multiselect whose option wrappers carry data-group: once one group holds a selection,
// the other groups are disabled until the selection is cleared.
export default class UIExclusiveGroups extends Controller {
  static targets = ["notice"];
  static values = { noticeTemplate: String };

  connect() {
    this.update();
  }

  update() {
    const active = this.activeGroup();
    this.groupedElements().forEach((element) => {
      const disabled = active !== null && element.dataset.group !== active;
      element.classList.toggle("pointer-events-none", disabled);
      element.classList.toggle("opacity-40", disabled);
      if (disabled) {
        element.setAttribute("aria-disabled", "true");
      } else {
        element.removeAttribute("aria-disabled");
      }
    });

    if (this.hasNoticeTarget) {
      const name = active === null ? null : this.groupName(active);
      this.noticeTarget.hidden = name === null;
      this.noticeTarget.textContent =
        name === null ? "" : this.noticeTemplateValue.replace("%{entity}", name);
    }
  }

  // Keyboard Enter clicks the highlighted option programmatically, which pointer-events can't stop
  guard(event) {
    if (event.target.closest("[aria-disabled='true']")) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  selectGroup(event) {
    const group = event.currentTarget.dataset.group;
    const values = this.optionElements()
      .filter((option) => option.closest("[data-group]")?.dataset.group === group)
      .map((option) => option.dataset.multiselectValue);
    this.multiselect()?.setSelectedValues(values);
    this.update();
  }

  activeGroup() {
    const selected = new Set(
      [...this.element.querySelectorAll("[data-multiselect-target='hiddenInput']")].map(
        (input) => input.value
      )
    );
    const option = this.optionElements().find(
      (element) =>
        selected.has(element.dataset.multiselectValue) && element.closest("[data-group]")
    );
    return option ? option.closest("[data-group]").dataset.group : null;
  }

  groupName(group) {
    const element = this.element.querySelector(`[data-group="${CSS.escape(group)}"]`);
    return element?.dataset.groupName ?? null;
  }

  groupedElements() {
    return [...this.element.querySelectorAll("[data-group]")];
  }

  optionElements() {
    return [...this.element.querySelectorAll("[data-multiselect-target='option']")];
  }

  multiselect() {
    const element = this.element.querySelector("[data-controller~='multiselect']");
    return element && this.application.getControllerForElementAndIdentifier(element, "multiselect");
  }
}
